import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { BottomSheet } from '../Common/BottomSheet';
import JourneyNextTip from '../Common/JourneyNextTip';
import NewSessionDialog from '../Session/NewSessionDialog';
import { storiesApi } from '../../api/stories';
import { journeyAccessibleName } from '../../utils/writerCopy';
import { journeyNextTip } from '../../utils/journeyNextTip';
import { navDisabledWhy } from '../../utils/navDisabledWhy';
import { isNotesPath, notesPathForAltitude } from '../../utils/seriesNotes';
import { plottrackPathForBook } from '../../utils/plottrackPath';
import { styleCardsPathForBook } from '../../utils/styleCardsPath';
import { pathRoomPathForBook } from '../../utils/pathRoomPath';
import { bookDiscoverySessionId, openBookDiscovery } from '../../utils/bookDiscoverySession';
import {
  BOOK_MENU_REACH,
  SIDEBAR_MAP_GROUPS,
  isChatSessionsPath,
  isDiscoveryPath,
  createSessionLaunch,
  isSeriesAltitude,
  journeyStageFromPath,
} from './sidebarMap';
import './JourneyHereChip.css';

const JOURNEY_GROUP = SIDEBAR_MAP_GROUPS.find((group) => group.id === 'journey');
const BOOK_GROUP = SIDEBAR_MAP_GROUPS.find((group) => group.id === 'your-book');
const JOURNEY_STAGES = JOURNEY_GROUP.items;
const BOOK_STOPS = BOOK_GROUP.items;
const CREATE_SESSION_ITEM = BOOK_MENU_REACH.find((item) => item.id === 'create');

/**
 * Narrow/phone you-are-here (#1129 / Erato concept 1).
 * High-contrast chip → half-sheet of the five journey stages.
 * Desktop/wide keeps the dual-line LeftNav rail.
 */
export default function JourneyHereChip() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentProject,
    currentStory,
    currentSession,
    currentSessionId,
    stories,
    projects,
    setCurrentStory,
  } = useApp();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [createSessionCtx, setCreateSessionCtx] = useState(null);

  const resolvedDiscoveryId = bookDiscoverySessionId({
    currentStory,
    currentProject,
    stories,
  });
  const discoveryIds = [
    resolvedDiscoveryId,
    currentStory?.discovery_session_id,
    currentProject?.discovery_session_id,
  ].filter(Boolean);
  const activeSessionId = currentSessionId || currentSession?.id || null;
  const atSeriesAltitude = isSeriesAltitude({
    isSeries: Boolean(currentProject?.is_series),
    pathname: location.pathname,
    currentStory,
    currentSession,
  });
  const sessionLaunch = createSessionLaunch({
    atSeriesAltitude,
    currentProject,
    currentStory,
    currentSession,
  });
  const canCreateSession = Boolean(sessionLaunch?.storyId);
  const navWhyArgs = {
    hasStory: Boolean(currentStory),
    currentProject,
    currentStory,
    canCreateSession,
    projects,
    pathname: location.pathname,
  };
  const stage = journeyStageFromPath(location.pathname, activeSessionId, discoveryIds);
  const exploreTip = !atSeriesAltitude
    ? journeyNextTip({
      stage,
      story: currentStory,
      storyId: currentStory?.id,
    })
    : null;

  const isStageCurrent = (item) => {
    if (item.id === 'discovery') {
      return isDiscoveryPath(location.pathname, activeSessionId, discoveryIds)
        || isChatSessionsPath(location.pathname, activeSessionId, discoveryIds);
    }
    if (item.id === 'plot-notebook' || item.path === '/dossiers') {
      return location.pathname === '/dossiers' || location.pathname.startsWith('/dossiers/');
    }
    if (item.id === 'path' || item.path === '/tasks' || item.path === '/path') {
      return location.pathname === '/tasks'
        || location.pathname.startsWith('/tasks/')
        || location.pathname === '/path'
        || location.pathname.startsWith('/path/');
    }
    if (item.kind === 'route') {
      return location.pathname.startsWith(item.path);
    }
    return false;
  };

  const isToolCurrent = (item) => {
    if (item.id === 'notes') return isNotesPath(location.pathname);
    if (item.path === '/bibles') return location.pathname.startsWith('/bibles');
    if (item.path) return location.pathname.startsWith(item.path);
    return false;
  };

  const currentStage = JOURNEY_STAGES.find(isStageCurrent)
    || BOOK_STOPS.find(isToolCurrent);
  const chipPlain = currentStage?.label || JOURNEY_GROUP.label;
  const chipName = chipPlain;

  const openDiscovery = async () => {
    try {
      await openBookDiscovery({
        currentStory,
        currentProject,
        stories,
        navigate,
        setCurrentStory,
        ensureDiscoverySession: storiesApi.ensureDiscoverySession,
      });
    } catch (error) {
      console.error('Failed to open Discovery:', error);
    }
  };

  const openCreateSession = () => {
    if (!sessionLaunch?.storyId) return;
    setCreateSessionCtx(sessionLaunch);
    setShowNewSession(true);
  };

  const selectItem = (item) => {
    if (navDisabledWhy(item, navWhyArgs)) return;
    setSheetOpen(false);
    switch (item.kind) {
      case 'route':
        if (item.id === 'plottrack') {
          navigate(plottrackPathForBook({
            pathname: location.pathname,
            currentStory,
            currentSession,
          }));
          return;
        }
        if (item.id === 'path') {
          navigate(pathRoomPathForBook({
            pathname: location.pathname,
            currentStory,
            currentSession,
          }));
          return;
        }
        if (item.id === 'style-cards') {
          const path = styleCardsPathForBook({
            pathname: location.pathname,
            currentStory,
            currentSession,
          });
          if (path) navigate(path);
          return;
        }
        if (item.path) navigate(item.path);
        return;
      case 'discovery':
        openDiscovery();
        return;
      case 'notes':
        if (currentProject?.id) {
          navigate(notesPathForAltitude({
            projectId: currentProject.id,
            storyId: currentStory?.id,
          }));
        }
        return;
      case 'create':
        openCreateSession();
        return;
      default:
        break;
    }
  };

  if (atSeriesAltitude) return null;

  return (
    <div className="journey-here">
      <button
        type="button"
        className="journey-here-chip"
        aria-expanded={sheetOpen}
        aria-haspopup="dialog"
        aria-label={chipName}
        data-testid="journey-here-chip"
        onClick={() => setSheetOpen(true)}
      >
        <span className="journey-here-chip__primary">{chipPlain}</span>
      </button>

      <BottomSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={JOURNEY_GROUP.label}
      >
        <div className="journey-sheet" data-testid="journey-half-sheet">
          <ol className="journey-sheet__stages">
            {[...JOURNEY_STAGES, ...BOOK_STOPS].map((item) => {
              const why = navDisabledWhy(item, navWhyArgs);
              const current = isStageCurrent(item) || isToolCurrent(item);
              const accessible = journeyAccessibleName(item.plain, item.label);
              return (
                <li key={item.id} data-journey-stage={item.id}>
                  <button
                    type="button"
                    className={[
                      'journey-sheet__stage',
                      current ? 'is-current' : '',
                      why ? 'is-disabled' : '',
                    ].filter(Boolean).join(' ')}
                    aria-current={current ? 'page' : undefined}
                    aria-label={accessible}
                    disabled={Boolean(why)}
                    onClick={() => selectItem(item)}
                  >
                    <span className="journey-sheet__stage-primary">{item.label}</span>
                  </button>
                  {item.id === 'discovery' && CREATE_SESSION_ITEM && (
                    <button
                      type="button"
                      className={[
                        'journey-sheet__create',
                        navDisabledWhy(CREATE_SESSION_ITEM, navWhyArgs) ? 'is-disabled' : '',
                      ].filter(Boolean).join(' ')}
                      aria-label={CREATE_SESSION_ITEM.label}
                      disabled={Boolean(navDisabledWhy(CREATE_SESSION_ITEM, navWhyArgs))}
                      onClick={() => selectItem(CREATE_SESSION_ITEM)}
                    >
                      {CREATE_SESSION_ITEM.label}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
          <JourneyNextTip tip={exploreTip} />
          <div className="journey-sheet__tools" data-testid="journey-sheet-book-tools">
            <p className="journey-sheet__tools-label">Also in this book</p>
            <div className="journey-sheet__tools-row">
              {BOOK_MENU_REACH.map((item) => {
                const why = navDisabledWhy(item, navWhyArgs);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={[
                      'journey-sheet__tool',
                      isToolCurrent(item) ? 'is-current' : '',
                      why ? 'is-disabled' : '',
                    ].filter(Boolean).join(' ')}
                    aria-current={isToolCurrent(item) ? 'page' : undefined}
                    disabled={Boolean(why)}
                    onClick={() => selectItem(item)}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </BottomSheet>

      {showNewSession && createSessionCtx?.storyId && (
        <NewSessionDialog
          storyId={createSessionCtx.storyId}
          projectId={createSessionCtx.projectId}
          storyName={
            (currentStory?.id === createSessionCtx.storyId && currentStory?.name)
              || (stories || []).find((s) => s.id === createSessionCtx.storyId)?.name
              || ''
          }
          seriesAltitude={false}
          onClose={() => {
            setShowNewSession(false);
            setCreateSessionCtx(null);
          }}
          onSessionCreated={(session) => {
            if (session?.id) navigate(`/chat/${session.id}`);
          }}
        />
      )}
    </div>
  );
}
